define((require) => {
    "use strict";

    var _                          = require("underscore"),
        $                          = require("jquery"),
        BreakpointAssignView       = require("editor/ui/experience/editor/BreakpointAssignView"),
        ActionSequence             = require("util/ActionSequence"),
        LoggedInUserState          = require("shared/ui/user/LoggedInUserState"),
        Tooltip                    = require("ui/common/Tooltip"),
        Lightbox                   = require("shared/ui/common/Lightbox"),
        Permissions                = require("shared/security/Permissions"),
        PublishableState           = require("util/PublishableState"),
        AddBreakpointAction        = require("ui/actions/AddBreakpointAction"),
        NavigateToExperienceAction = require("ui/actions/NavigateToExperienceAction"),
        GroupNavView               = require("editor/ui/experience/editor/GroupNavView");

    var NO_PERMISSION_MESSAGE = "You do not have permission to delete Experiences.",
        NOT_ALLOWED_MESSAGE = "This Experience cannot be deleted because it is pushed and assigned to a Breakpoint. " +
            "Unassign it from the Breakpoint and push the Group again if you want to delete the Experience.";

    /**
     * @class GroupBreakpointNavView
     *
     * A view that represents all variant tabs for a specific breakpoint group.
     *
     * @cfg {BreakpointGroup} model The BreakpointGroup model
     */
    return GroupNavView.extend({

        _breakpointsChanged: null,

        ui: _.defaults({
            assignBreakpoints: ".assign-breakpoints"
        }, GroupNavView.prototype.ui),

        templateHelpers: function() {
            return _.extend(this.__super(), {
                breakpointsChanged: this._breakpointsChanged
            });
        },

        events: _.defaults({
            "click .assign-breakpoints": "openAssignModal"
        }, GroupNavView.prototype.events),

        initialize: function() {
            this.__super();

            var breakpointCollection = LoggedInUserState.get("companySettings").get("breakpoints");

            this.lowerBoundsInSetting = breakpointCollection.getLowerBounds();
        },

        onBeforeRender: function() {
            this.__super();

            // Checks if the breakpoint setting has been updated by comparing the breakpoints in company setting
            // against the ones in the breakpoint group model
            this._breakpointsChanged = !this.model.compareThresholds(this.lowerBoundsInSetting);
        },

        onRender: function() {
            this.__super();

            this._updateDeleteIcons();
            this.ui.firstLine.after(this.getAssignIconHtml());
        },

        // Kind of a hack to inject the assign icon html to the template, since there is no better way to
        // inherit a template view with backbone
        getAssignIconHtml: function() {
            var assignIconHtml = "<div class='vertical-line'></div><div class='assign-breakpoints'>";

            if (this._breakpointsChanged) {
                assignIconHtml += "<i class='glyphicon glyphicon-exclamation-sign'></i>";
            }

            return assignIconHtml + "</div>";
        },

        onShow: function() {
            this.__super();

            var tip =  {
                content: "Assign Breakpoint Variants.",
                width: 180
            };

            if (this._isSpectator) {
                tip = {
                    content: "Assigned Breakpoint Variants",
                    width: 190
                };
            } else if (this._breakpointsChanged) {
                tip = {
                    content: "The Breakpoint Settings have been changed.</br>" +
                             "Update to the new Breakpoints or assign Breakpoint Variants.",
                    width: 200
                };
            }

            Tooltip.create(this.ui.assignBreakpoints, tip);
        },

        /**
         * Calls the general push logic in the super class, and update delete icons afterwards.
         *
         * @override
         * @private
         */
        _pushGroup: function() {
            this.__super().done(this._updateDeleteIcons.bind(this));
        },

        /**
         * Check which assets need to be pushed when push group button is clicked
         *
         * @returns {Object}    An object where indexes map to the actual asset
         * @private
         */
        toBePushedAssets: function() {
            return _.reduce(this.model.getSortedAssets(), (assets, asset, index) => {
                if (asset.hasNonPushedChanges()) {
                    assets["#" + (index + 1)] = asset;
                }

                return assets;
            }, {});
        },

        /**
         * Used to update delete icons after push. if we call render instead, it will result in an
         * flashing rendering effect in IE. See bug report MOS-2284 for more info.
         */
        _updateDeleteIcons: function() {
            var deleteButtons = this.ui.deleteBtn,
                self = this,
                width = self._isSpectator ? 300 : 400,
                isDeletable = false,
                experience = null;

            if (self._isSpectator) {
                deleteButtons.toggleClass("disabled", true);
                Tooltip.create(deleteButtons, {width: width, content: NO_PERMISSION_MESSAGE});

                return;
            }

            deleteButtons.each(function() {
                var $this = $(this),
                    experienceId = $this.data("experience-id");

                experience = self.model.findAssetById(experienceId);
                isDeletable = self.isDeletable(experience);

                $this.toggleClass("delete", isDeletable);
                $this.toggleClass("disabled", !isDeletable);

                if (isDeletable) {
                    Tooltip.remove($this);
                } else {
                    Tooltip.create($this, {width: width, content: NOT_ALLOWED_MESSAGE});
                }
            });
        },

        /**
         * Check if the current role has permission to delete an experience. If an experience is assigned to a breakpoint
         * and it is pushed, it is not allowed to delete. Note: also checking both the spectator and publisher role.
         *
         * @param asset       The asset/experience to be checked
         * @returns {boolean} True the current user is allowed to delete the asset, otherwise not allowed
         */
        isDeletable: function(asset) {
            var entries   = this.options.pushed.entries(),
                spectator = Permissions.Privileges.CREATE.cannot(),
                publisher = Permissions.Privileges.PUBLISH.can(),
                pushed    = asset.hasAtLeastState(PublishableState.PUSHED),
                deletable = (pushed && publisher) || (!pushed && !spectator);

            // If an asset is pushed and is assigned to a breakpoint, then it is not deletable
            var isPushedAndAssigned = _.find(entries, (entry) => {
                return (entry[0] && entry[0].get("id") === asset.id) && entry[1];
            });

            return deletable && !isPushedAndAssigned;
        },

        /**
         * Open a modal where experience variants can be assigned to breakpoints
         */
        openAssignModal: function() {
            var breakpointAssignView = new BreakpointAssignView({
                model: this.model
            });

            var lightboxOptions = {
                win: false,
                css: {
                    width: 700
                },
                editorEventProperties: {
                    groupId: this.model.get("id"),
                    groupType: this.model.get("type"),
                    groupVersion: this.model.get("version"),
                    groupPushedVersion: this.model.get("pushedVersion"),
                    modalType: "BreakpointAssign",
                    triggerOrigin: "BreakpointAssignModal"
                }
            };

            var box = new Lightbox(breakpointAssignView, lightboxOptions);
            box.open();
        },
        addVariantFromOtherExperience: function(currentExperience) {
            new ActionSequence()
                .add(new AddBreakpointAction(this.model, currentExperience, this.options.project))
                .add(new NavigateToExperienceAction())
                .execute();
        },
        addVariant: function() {
            new ActionSequence()
                .add(new AddBreakpointAction(this.model, this._currentExperience, this.options.project))
                .add(new NavigateToExperienceAction())
                .execute();
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/editor/GroupBreakpointNavView.js